@if (!isConfigured()) {
  <div class="table-panel__configure">
    <h4>Setup</h4>
    <mat-divider></mat-divider>
    <mat-form-field>
      <mat-label>Select the table type</mat-label>
      <mat-select
        [(value)]="tableType"
        (selectionChange)="onTableTypeChanged()"
        (openedChange)="onTableTypeOpenedChanged($event)"
        [appAuthorize]="canEdit"
        denyAction="disable"
      >
        <mat-form-field class="sticky top-0 z-10 w-full p-2 pb-0">
          <input
            [appAuthorize]="canEdit"
            denyAction="disable"
            matInput
            [(ngModel)]="searchTxt"
            placeholder="Search..."
            autocomplete="off"
          />
        </mat-form-field>
        @if (!searchTxt) {
          <mat-option>None</mat-option>
        }
        <span class="dropdown-title">Related Objects</span>
        @for (objectType of objectTypes | tsFilterBy: [] : searchTxt : filterFn; track objectType.name) {
          @if (!hideObjectTypeBasedOnArchivedLinkedField(objectType)) {
            <mat-option [value]="objectType.name">
              {{ objectType.label }}
            </mat-option>
          }
        }
        @if (hasRecordTypeFields) {
          <span class="dropdown-title">Related To Fields</span>
          @for (recordField of recordTypeFields | tsFilterBy: [] : searchTxt : filterFieldsFn; track recordField.name) {
            <mat-option [value]="recordField.name">
              {{ recordField.label }}
            </mat-option>
          }
        }
      </mat-select>
    </mat-form-field>
    @if (tableType) {
      <mat-form-field>
        <mat-label>Select the columns</mat-label>
        <mat-select [(value)]="initialFields" multiple>
          @for (field of fields$ | async; track field.name) {
            <mat-option [value]="field.name">{{ field.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }
    <button
      [disabled]="!canConfirmSetup"
      [appAuthorize]="canEdit"
      denyAction="disable"
      (click)="confirmTableType()"
      mat-flat-button
      color="primary"
    >
      <span>Confirm</span>
    </button>
  </div>
} @else {
  <div class="flex justify-between my-3 px-3">
    <app-column-selector
      [items]="fields$ | async"
      (onSave)="onSaveColumns($event)"
      [appAuthorize]="canEdit"
      denyAction="hide"
      [disabled]="readonly || !items().length"
    ></app-column-selector>
    <div class="flex gap-x-1">
      <ts-button
        (click)="discardChanges()"
        [disabled]="!hasChangesToSave() || savingTable"
        [appAuthorize]="canEdit"
        denyAction="hide"
        type="stroked"
        color="accent"
        >Cancel</ts-button
      >
      <ts-button
        [appAuthorize]="canEdit"
        denyAction="hide"
        (click)="onSave()"
        [loading]="savingTable"
        [disabled]="!hasChangesToSave()"
        >Apply changes</ts-button
      >
    </div>
  </div>
  @if (items(); as items) {
    <mat-progress-bar mode="indeterminate" [class.hidden]="!isLoading"></mat-progress-bar>
    @if (newTable() && items.length) {
      <ts-table
        [gridOptions]="gridOptions"
        [relatedToRedirectConfig]="relatedToRedirectConfig"
        [columnDefs]="columnDefs"
        [rowData]="items"
        [data]="data"
        [hasOpenDetailsColumn]="true"
        [readOnly]="readonly"
        (gridReady)="onGridReady($event)"
        (openDetails)="handleOpenDetails($event)"
        (onColumnMove)="onColumnMoved($event)"
        (cellEditingStopped)="onCellEditingStopped($event)"
        (sortChanged)="onSortChanged($event)"
      ></ts-table>
    } @else if (!newTable() && items.length) {
      <section class="overflow-auto">
        <table
          mat-table
          matSort
          [matSortActive]="sortState[0]?.field"
          [matSortDisableClear]="true"
          [matSortDirection]="sortState[0]?.order"
          [dataSource]="items"
          cdkDropList
          cdkDropListOrientation="horizontal"
          (cdkDropListDropped)="drop($event)"
          (matSortChange)="onMatSortChanged($event)"
          [ngClass]="{ loading: isLoading }"
          class="item-table"
        >
          <ng-container [matColumnDef]="prefixColumn">
            <th mat-header-cell *matHeaderCellDef [class.display-none]="isCurrentProjectTable"></th>
            <td mat-cell *matCellDef="let row" [class.display-none]="isCurrentProjectTable">
              @switch (tableType) {
                @case ('Property') {
                  <a class="open-anchor" matTooltip="Go to Property" [routerLink]="['/properties', row.id]">
                    <mat-icon class="open-icon">open_in_new</mat-icon>
                  </a>
                }
                @case ('Project') {
                  <a class="open-anchor" matTooltip="Go to Deal" [routerLink]="['/projects', row.pipeline_id, row.id]">
                    <mat-icon class="open-icon">open_in_new</mat-icon>
                  </a>
                }
                @default {
                  <a
                    class="open-anchor pointer"
                    [matTooltip]="'Go to record'"
                    (click)="goToRecordBasedOnType(tableType, row)"
                    ><mat-icon class="open-icon">open_in_new</mat-icon>
                  </a>
                }
              }
            </td>
            <td mat-footer-cell *matFooterCellDef class="item-table__footer-td"></td>
          </ng-container>
          @for (column of columns; track column.name) {
            <ng-container [matColumnDef]="column.name">
              <th
                mat-header-cell
                mat-sort-header
                *matHeaderCellDef
                cdkDrag
                cdkDragLockAxis="x"
                [cdkDragDisabled]="!canEdit"
              >
                <mat-icon cdkDragHandle class="drag-icon">drag_indicator</mat-icon>
                {{ column.label }}
              </th>
              <td mat-cell *matCellDef="let row" #editableField [class.locked]="readonly">
                <app-editable
                  [fieldDef]="column.fieldDef"
                  [model]="row"
                  [data]="data"
                  [meta]="{ origin: 'app-table' }"
                  [readonly]="readonly || !canEdit"
                  [appAuthorize]="canEdit"
                  denyAction="disable"
                  (onUpdate)="onFieldUpdate($event, row, column.fieldDef.name)"
                ></app-editable>
              </td>
              <td mat-footer-cell *matFooterCellDef class="item-table__footer-td">
                @if (getSumOrAvg(column); as sumOrAvgValue) {
                  <span> {{ sumOrAvgValue | titlecase }}</span>
                  <button mat-icon-button [matMenuTriggerFor]="menu">
                    <mat-icon svgIcon="expand_more" class="icon-size-12"></mat-icon>
                  </button>
                  <mat-menu #menu="matMenu">
                    <button mat-menu-item (click)="toggleSumOrAvg(column, 'sum')">Sum</button>
                    <button mat-menu-item (click)="toggleSumOrAvg(column, 'avg')">Avg</button>
                  </mat-menu>
                }
              </td>
            </ng-container>
          }
          <tr mat-header-row *matHeaderRowDef="displayColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayColumns"></tr>
          <tr mat-footer-row *matFooterRowDef="displayColumns" [class.display-none]="isCurrentProjectTable"></tr>
        </table>
      </section>
    } @else {
      <section class="flex flex-col items-center justify-center h-full">
        <mat-icon class="icon-size-40" [svgIcon]="'info'"></mat-icon>
        <p class="text-2xl text-secondary font-medium mt-4">No results found</p>
        <p class="text-sm text-secondary mt-2">
          Make sure you have selected a valid and unarchived linked field or object type.
        </p>
      </section>
    }
  } @else {
    <mat-spinner></mat-spinner>
  }
}
